/**
 * Admin user management API service.
 *
 * Encapsulates all admin CRUD API calls so page components
 * don't contain raw fetch logic.
 */

import { authFetch } from "./authService";
import { getApiBaseUrl } from "../../../host/utils/http";

const API_BASE = () => `${getApiBaseUrl()}/secure-auth`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract a readable error string from a FastAPI error response body.
 */
export const extractError = (body, fallback = "Something went wrong") => {
  if (!body) return fallback;
  const d = body.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((e) => e.msg || JSON.stringify(e)).join("; ");
  }
  return String(d);
};

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * Fetch paginated user list (admin only).
 * @param {number} page - Page number (1-based).
 * @param {number} pageSize - Items per page.
 * @returns {Promise<{users: Array, total: number}>}
 */
export const fetchUsers = async (page = 1, pageSize = 100) => {
  const res = await authFetch(
    `${API_BASE()}/admin/users?page=${page}&page_size=${pageSize}`
  );
  if (!res.ok) {
    if (res.status === 403) throw new Error("Admin access required");
    throw new Error("Failed to load users");
  }
  return res.json();
};

/**
 * Create a new user (admin only).
 * @param {{ first_name: string, last_name: string, email: string, roles: string[], password?: string }} userData
 * @returns {Promise<Object>} Created user data (includes login_id, temporary_password).
 */
export const createUser = async (userData) => {
  const body = {
    first_name: userData.first_name.trim(),
    last_name: userData.last_name.trim(),
    email: userData.email.trim(),
    roles: userData.roles,
  };
  if (userData.password && userData.password.trim()) {
    body.password = userData.password.trim();
  }

  const res = await authFetch(`${API_BASE()}/admin/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const d = await res.json().catch(() => null);
    throw new Error(extractError(d, "Failed to create user"));
  }
  return res.json();
};

/**
 * Update an existing user (admin only).
 * @param {string} userId - UUID of user to update.
 * @param {{ email?: string, roles?: string[], is_active?: boolean }} updates
 * @returns {Promise<Object>}
 */
export const updateUser = async (userId, updates) => {
  const res = await authFetch(`${API_BASE()}/admin/users/${userId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => null);
    throw new Error(extractError(d, "Failed to update user"));
  }
  return res.json();
};

/**
 * Soft-delete (deactivate) a user (admin only).
 * @param {string} userId - UUID of user to delete.
 * @returns {Promise<Object>}
 */
export const deleteUser = async (userId) => {
  const res = await authFetch(`${API_BASE()}/admin/users/${userId}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const d = await res.json().catch(() => null);
    throw new Error(extractError(d, "Failed to delete"));
  }
  return res.json();
};

/**
 * Reset a user's password (admin only).
 * @param {string} userId - UUID of user.
 * @param {string} newPassword - New password to set.
 * @returns {Promise<Object>}
 */
export const resetUserPassword = async (userId, newPassword) => {
  const res = await authFetch(
    `${API_BASE()}/admin/users/${userId}/reset-password`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: userId,
        new_password: newPassword,
        reset_token: "admin",
      }),
    }
  );
  if (!res.ok) {
    const d = await res.json().catch(() => null);
    throw new Error(extractError(d, "Failed to reset password"));
  }
  return res.json();
};
